<template>
  <div>
    <div
      v-if="moduleGraphStore.selectedNodeInfo && moduleGraphStore.selectedNodeId"
      class="mt-3 mb-3"
    >
      <div
        v-if="![START_NODE_TYPE, END_NODE_TYPE].includes(selectedType)"
        class="is-flex is-justify-content-space-between mb-2"
      >
        <BaseLabel
          v-if="moduleGraphStore.selectedNodeInfo.isIgnoreSkippedNodeVariablesDynamic"
          class="mt-2"
        >
          Ignore skipped node values
        </BaseLabel>
        <b-switch
          v-else
          v-model="moduleGraphStore.selectedNodeInfo.ignoreSkippedNodeVariables"
          @input="updateSidebarInfo()"
        >
          Ignore skipped node values
        </b-switch>
        <b-switch
          v-model="moduleGraphStore.selectedNodeInfo.isIgnoreSkippedNodeVariablesDynamic"
          @input="updateSidebarInfo()"
        >
          Dynamic
        </b-switch>
      </div>
      <Editor
        v-if="moduleGraphStore.selectedNodeInfo.isIgnoreSkippedNodeVariablesDynamic"
        v-model="moduleGraphStore.selectedNodeInfo.ignoreSkippedNodeVariables"
        :input="true"
        :formula="true"
        class="mb-2"
        @input="updateSidebarInfo()"
      />
      <DynamicValueSwitch
        v-if="selectedType === SCREEN_NODE_TYPE"
        v-model="moduleGraphStore.selectedNodeInfo.hasEagerFormValidation"
        :is-dynamic="moduleGraphStore.selectedNodeInfo.isEagerFormValidationDynamic"
        label="Eager Form Validations"
        class="mb-3"
        @input="updateSidebarInfo()"
        @update-dynamic-switch="updateSidebarInfoByKey('isEagerFormValidationDynamic', $event)"
      />
    </div>
    <ActionSidebar
      v-if="selectedType === ACTION_NODE_TYPE"
      v-model="sidebarInfo"
    />
    <IntegrationSidebar
      v-else-if="selectedType === INTERGRATION_NODE_TYPE"
      v-model="sidebarInfo"
    />
    <ScreenSidebar
      v-else-if="selectedType === SCREEN_NODE_TYPE"
      v-model="sidebarInfo"
    />
    <ConnectionSidebar v-else-if="selectedType === CONNECTION" />
    <StartSidebar
      v-else-if="selectedType === START_NODE_TYPE"
      v-model="sidebarInfo"
    />
    <div v-else>
      <b-notification
        type="is-primary is-light"
        :closable="false"
        class="mt-4 has-text-centered"
      >
        Please select a node from the builder.
      </b-notification>
    </div>
    <b-message
      v-if="errorMessages.length"
      size="is-small"
      style="position: fixed; bottom: 20px; "
      type="is-danger"
      has-icon
    >
      <ul>
        <li
          v-for="(error, index) in errorMessages"
          :key="index"
        >
          {{ error.message }}
        </li>
      </ul>
    </b-message>
  </div>
</template>

<script >
// libs
import { ref, computed, set, watch } from '@vue/composition-api';
import { debouncedWatch } from '@vueuse/core';
// components
import ActionSidebar from '@/modules/builder/components/module-sidebar/action/ActionSidebar.vue';
import IntegrationSidebar from '@/modules/builder/components/module-sidebar/integration/IntegrationSidebar';
import ScreenSidebar from '@/modules/builder/components/module-sidebar/screen/ScreenSidebar';
import ConnectionSidebar from '@/modules/builder/components/module-sidebar/connection/ConnectionSidebar';
import StartSidebar from '@/modules/builder/components/module-sidebar/start/StartSidebar';
import Editor from '@/modules/core/components/wysiwyg/Editor';
import BaseLabel from '@/modules/core/components/generics/BaseLabel.vue';
import DynamicValueSwitch from '@/modules/core/components/DynamicValueSwitch.vue';
// stores
import { useModuleGraphStore } from '@/modules/builder/store/moduleGraphStore';
import { useModuleStore } from '@/modules/builder/store/moduleStore';
import { useRoute } from '@/hooks/vueRouter';
// composables
import { useModuleSidebar } from '@/modules/builder/components/module-sidebar/moduleSidebar';
// others
import { ACTION_NODE_TYPE, END_NODE_TYPE, INTERGRATION_NODE_TYPE, SCREEN_NODE_TYPE, START_NODE_TYPE, CONNECTION } from '@/modules/builder/constants/moduleGraphNodes';

//-- use composables --//
const __sfc_main = {};
__sfc_main.setup = (__props, __ctx) => {
  const moduleGraphStore = useModuleGraphStore();
  const moduleStore = useModuleStore();
  const route = useRoute();
  const {
    fetchSidebarInfo,
    saveSidebarInfo
  } = useModuleSidebar();
  const selectedType = computed(() => {
    if (moduleGraphStore.selectedNodeType) {
      return moduleGraphStore.selectedNodeType;
    } else if (moduleGraphStore.selectedConnectionId) {
      return CONNECTION;
    }
    return '';
  });
  const selectedNodeName = computed(() => moduleGraphStore.selectedNode ? moduleGraphStore.selectedNode?.data?.name : '');
  const errorMessages = ref([]);
  const sidebarInfo = computed({
    get() {
      const {
        appId
      } = route.params;
      const {
        moduleId
      } = moduleStore;
      let info = moduleGraphStore.nodeInfo[moduleGraphStore.selectedNodeId] || {};
      info = {
        ...info,
        application_id: info.application_id || appId,
        module_id: info.module_id || moduleId
      };
      return info;
    },
    set(newValue) {
      saveSidebarInfo(moduleGraphStore.selectedNodeId, newValue);
    }
  });
  const updateVariablesName = async () => {
    try {
      const {
        appId
      } = route.params;
      const {
        moduleId
      } = moduleStore;
      const variables = moduleStore.moduleVariables.filter(variable => variable.node_id === moduleGraphStore.selectedNodeId).map(variable => {
        const {
          id,
          ...restVariable
        } = variable;
        const [_, ...nameList] = restVariable.name.split(' > '); // first item in array is outdated node name which has to be updated and should not be sent to create variable API
        restVariable.name = nameList.join(' > ');
        return restVariable;
      });
      if (variables?.length) {
        await moduleStore.createVariable(appId, moduleId, {
          node_id: moduleGraphStore.selectedNodeId,
          node_name: selectedNodeName.value,
          module_id: moduleId,
          variables
        });
      }
    } catch (err) {
      console.error(err);
    }
  };
  debouncedWatch(() => selectedNodeName.value, () => {
    if (selectedNodeName.value) {
      // on change of node name, update variables name of that node
      updateVariablesName();
    }
  }, {
    debounce: 1000
  });
  watch(() => moduleGraphStore.selectedNodeId, async () => {
    if (moduleGraphStore.selectedNodeId) {
      const nodeId = moduleGraphStore.selectedNodeId;
      await fetchSidebarInfo(nodeId);
    }
  }, {
    immediate: true
  });
  const updateSidebarInfoByKey = (key, value) => {
    if (key in moduleGraphStore.selectedNodeInfo) {
      moduleGraphStore.selectedNodeInfo[key] = value;
    } else {
      set(moduleGraphStore.selectedNodeInfo, key, value);
    }
    updateSidebarInfo();
  };
  const updateSidebarInfo = () => {
    saveSidebarInfo(moduleGraphStore.selectedNodeId, moduleGraphStore.selectedNodeInfo);
  };
  return {
    ACTION_NODE_TYPE,
    END_NODE_TYPE,
    INTERGRATION_NODE_TYPE,
    SCREEN_NODE_TYPE,
    START_NODE_TYPE,
    CONNECTION,
    moduleGraphStore,
    selectedType,
    errorMessages,
    sidebarInfo,
    updateSidebarInfoByKey,
    updateSidebarInfo
  };
};
__sfc_main.components = Object.assign({
  BaseLabel,
  Editor,
  DynamicValueSwitch,
  ActionSidebar,
  IntegrationSidebar,
  ScreenSidebar,
  ConnectionSidebar,
  StartSidebar
}, __sfc_main.components);
export default __sfc_main;
</script>
